import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@sharpana/auth/authHooks';
import * as Sentry from '@sentry/react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faThumbsUp, faThumbsDown, faMeh } from '@fortawesome/free-solid-svg-icons';
import { UserActivityType, ResourceType } from '../RecommendationFeed/types/activity_types';
import { getOrRateOnboardingItem, OnboardingItemResponse } from '../RecommendationFeed/api_client/recommendation_api';
import { VideoRecommendation, YtSharpRecommendation, RecommendationItem } from '../RecommendationFeed/api';
import VideoRecommendationCard from '../RecommendationFeed/VideoRecommendationCard';
import { YtSharp } from '../RecommendationFeed/components/YtSharp';
import {
  onboardingContainer,
  itemRankingOnboardingContainer,
  onboardingTitle,
  onboardingSubtitle,
  buttonContainer,
  continueButton,
  stepIndicator,
  loadingSpinner,
  completionMessage,
  itemContainer,
  ratingContainer,
  ratingButton,
  dislikeButton,
  neutralButton,
  likeButton,
  buttonIcon,
  buttonText,
  errorText
} from './onboarding_styles';




export function ItemRanking() {
  // We use useUser() to ensure authentication, even if not directly using the user object
  useUser();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 769);
  const stepIndicatorRef = useRef<HTMLDivElement>(null);
  const [currentItem, setCurrentItem] = useState<RecommendationItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [progress, setProgress] = useState({ 
    current_sample_number: 0, 
    normal_total_samples: 5, 
    onboarding_state: 'in_progress' as 'in_progress' | 'extra_sample' | 'complete'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle the item response
  const handleItemResponse = useCallback((response: OnboardingItemResponse) => {
    if (response.success && response.next_item) {
      setCurrentItem(response.next_item);
      console.log('Current item:', response.next_item)
      if (response.progress) {
        setProgress(response.progress);
      }
      // Scroll to step indicator when new item is loaded
      stepIndicatorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (response.success && !response.next_item) {
      // No more items to rank, onboarding is complete
      // Set progress as complete to trigger the useEffect that navigates to feed
      setProgress(prev => ({ ...prev, onboarding_state: 'complete' }));
      
    } else {
      setError(response.error || 'Failed to load the next item');
      // Reset loading and submitting states on error
      setIsLoading(false);
      setIsSubmitting(false);
    }
  }, [navigate]);

  // Fetch the next item to rank - wrapped in useCallback to avoid dependency issues
  const fetchInitialItem = useCallback(async () => {
    try {
      setIsLoading(true);
      setIsSubmitting(false); // Reset submitting state when fetching new items
      setError('');
      setCurrentItem(null); // Reset current item to avoid showing stale data
      
      const response = await getOrRateOnboardingItem();
      handleItemResponse(response);
    } catch (err) {
      console.error('Error fetching next item:', err);
      setError('Failed to load the next item. Please try again.');
      Sentry.captureException(err, {
        tags: { action: 'getOrRateOnboardingItem', component: 'ItemRanking' }
      });
    } finally {
      setIsLoading(false);
    }
  }, [handleItemResponse]);  // Include handleItemResponse in the dependency array

  // Fetch the first item to rank when the component mounts
  useEffect(() => {
    fetchInitialItem();
    
    // Handle responsive layout
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 769);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [fetchInitialItem]);

  // Navigate to feed when onboarding state is complete
  useEffect(() => {
    if (progress.onboarding_state === 'complete') {
      navigate('/feed');
    }
  }, [progress.onboarding_state, navigate]);



  // Handle item rating
  const handleRating = async (activityType: UserActivityType) => {
    if (!currentItem || isSubmitting) return;

    try {
      setIsSubmitting(true);
      setError('');

      const response = await getOrRateOnboardingItem({
        resource_id: currentItem.id,
        resource_type: currentItem.type as ResourceType,
        activity_type: activityType
      });

      handleItemResponse(response);
    } catch (err) {
      console.error('Error rating item:', err);
      setError('Failed to submit your rating. Please try again.');
      Sentry.captureException(err, {
        tags: { action: 'getOrRateOnboardingItem', component: 'ItemRanking', activity_type: activityType }
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  

  // Render loading state
  if (isLoading) {
    return (
      <div className={onboardingContainer} role="main">
        <h1 className={onboardingTitle}>Loading your recommendations...</h1>
        <div className={loadingSpinner}></div>
      </div>
    );
  }



  // Show completion message when onboarding state is complete
  if (progress.onboarding_state === 'complete') {
    return (
      <div className={onboardingContainer} role="main">
        <h1 className={onboardingTitle}>Onboarding Complete!</h1>
        <p className={onboardingSubtitle}>
          Thank you for your ratings. We're personalizing your feed based on your preferences.
        </p>
        <div className={completionMessage}>
          <div className={loadingSpinner}></div>
          <p>Redirecting to your personalized feed...</p>
        </div>
      </div>
    );
  }

  // Render item ranking UI
  return (
    <div className={itemRankingOnboardingContainer} role="main" style={{ overflowY: 'auto', height: '100vh' }}>
      <div ref={stepIndicatorRef} className={stepIndicator}>
        Step 2 of 2: Rate samples
      </div>
      
      <p className={onboardingSubtitle}>
        Please rate your interest in the following items to fine-tune your feed.
      </p>

      {/* Progress bar or 'Just one more...' message */}
      <div style={{
        width: '100%',
        maxWidth: '600px',
        margin: '5px auto',
        textAlign: 'center'
      }}>
        {progress.onboarding_state === 'extra_sample' ? (
          <div style={{ 
            fontSize: '1.125rem', 
            color: '#a0a0a0', 
            textAlign: 'center', 
            marginBottom: '1rem',
            fontWeight: 'bold'
          }}>
            Just one more...
          </div>
        ) : (
          <>
            <style>
              {`
                progress {
                  /* Reset the default appearance */
                  appearance: none;
                  -webkit-appearance: none;
                  width: 92%;
                  height: 10px;
                  margin-bottom: 8px;
                }
                progress::-webkit-progress-bar {
                  background-color: #333333;
                  border-radius: 8px;
                }
                progress::-webkit-progress-value {
                  background: linear-gradient(90deg, #4776E6 0%, #8E54E9 100%);
                  border-radius: 8px;
                }
                progress::-moz-progress-bar {
                  background: linear-gradient(90deg, #4776E6 0%, #8E54E9 100%);
                  border-radius: 8px;
                }
              `}
            </style>
            <progress 
              value={progress.current_sample_number} 
              max={progress.normal_total_samples}
              aria-label={`${progress.current_sample_number} of ${progress.normal_total_samples} items rated`}
            />
            <div style={{ 
              fontSize: '0.875rem', 
              color: '#a0a0a0', 
              textAlign: 'center', 
              marginBottom: '1rem' 
            }}>
              {progress.current_sample_number} of {progress.normal_total_samples} items rated
            </div>
          </>
        )}
      </div>

      {error && (
        <div className={errorText} role="alert" aria-live="polite">
          {error}
        </div>
      )}

      {!isLoading && !currentItem && !error && (
        <div className={errorText} role="alert" aria-live="polite">
          No recommendation items available. Please try again later.
          <div className={buttonContainer} style={{ marginTop: '20px' }}>
            <button 
              className={continueButton(true)} /* Pass true to indicate we have enough categories */
              onClick={() => navigate('/feed')}
              aria-label="Continue to feed"
            >
              Continue to Feed
            </button>
          </div>
        </div>
      )}
      
      {/* Loading indicator during submission */}
      {isSubmitting && (
        <div className={loadingSpinner} style={{ margin: '20px auto' }} aria-label="Submitting rating..."></div>
      )}

      {currentItem && !isSubmitting && (
        <div className={itemContainer}>
          {/* Display item based on type */}
          {currentItem.type === ResourceType.YT_FULL_VIDEO && (
            <VideoRecommendationCard 
              video={currentItem as VideoRecommendation}
              onboardingMode={true}
            />
          )}

          {currentItem.type === ResourceType.YT_SHARP && (
            <YtSharp 
              recommendation={currentItem as YtSharpRecommendation}
              onboardingMode={true}
            />
          )}
          
          {/* Spacer div for proper spacing above rating buttons */}
          <div style={{ 
            height: isMobile ? '70px' : '85px', 
            width: '100%', 
            flexShrink: 0
          }} />

          {/* Rating buttons */}
          <div className={ratingContainer}>
            
            <button 
              className={`${dislikeButton} ${ratingButton}`}
              onClick={() => handleRating(UserActivityType.DISLIKED)}
              disabled={isSubmitting}
              aria-label="Dislike"
              data-testid="dislike-button"
            >
              <FontAwesomeIcon icon={faThumbsDown} className={buttonIcon} />
              <span className={buttonText}>Not interested</span>
            </button>
            
            <button 
              className={`${neutralButton} ${ratingButton}`}
              onClick={() => handleRating(UserActivityType.NEUTRAL_RATING)}
              disabled={isSubmitting}
              aria-label="Neutral"
              data-testid="neutral-button"
            >
              <FontAwesomeIcon icon={faMeh} className={buttonIcon} />
              <span className={buttonText}>Neutral</span>
            </button>
            
            <button 
              className={`${likeButton} ${ratingButton}`}
              onClick={() => handleRating(UserActivityType.LIKED)}
              disabled={isSubmitting}
              aria-label="Like"
              data-testid="like-button"
            >
              <FontAwesomeIcon icon={faThumbsUp} className={buttonIcon} />
              <span className={buttonText}>Interested</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ItemRanking;
